import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFormActionButtons, getFormTitle, transformFormData, fetchSoDuTonKho } from '../../../utils';
import { documentSelectionRows, normalizedFormValues } from '../utils';
import { useTableRows } from '@/components/custom/erp/input-table-v2';
import { useHoaDonBanHangSearchContext } from '../../../context';
import { formSchema, initialFormValues } from '../../../schema';
import { getDocumentsColumns } from '../get-documents-columns';
import type { AccountRow, FormDialogProps } from '../types';
import { useDetailSection } from './useDetailSection';
import { useFormFieldState } from '../../../hooks';
import { useAuth } from '@/contexts/auth-context';
import type { DetailRow } from './useDetailRows';

const EMPTY_CONSTRAINTS: BanHangRecord[] = [];

export const useFormDialog = ({
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [activeTab, setActiveTab] = useState('info');
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const formState = useFormFieldState(initialData);
  const { state, actions } = formState;
  const { transactionOptions } = useHoaDonBanHangSearchContext();
  const { entity, entityUnit } = useAuth();
  const detail = useDetailSection({
    initialRows: initialData?.chi_tiet || [],
    discountType: state.loai_ck,
    discountPool: state.ck_tl_nt
  });
  const account = useTableRows<AccountRow>({
    initialRows: initialData?.thong_tin_thanh_toan || [],
    defaultField: 'ma_httt'
  });
  const constraints = useMemo(
    () => state.taiKhoan?.constraints ?? state.khachHang?.account_data?.constraints ?? EMPTY_CONSTRAINTS,
    [state.khachHang?.account_data?.constraints, state.taiKhoan?.constraints]
  );
  const schema = useMemo(() => formSchema(constraints), [constraints]);

  const submit = useCallback(
    (data: BanHangPayload) => {
      const values = normalizedFormValues({ data, formMode, initialData, transactionOptions });
      const payload = transformFormData(
        values,
        state,
        detail.rows,
        account.rows,
        formMode,
        detail.totals,
        entityUnit,
        initialData?.chi_tiet
      );

      onSubmit?.({ data: payload, detailRows: detail.rows });
    },
    [account.rows, detail.rows, detail.totals, entityUnit, formMode, initialData, onSubmit, state, transactionOptions]
  );

  const documentSelection = useCallback(
    (searchedData: BanHangPayload) => {
      actions.setCkYn(true);
      actions.setLoaiCk('1');
      if (searchedData.customer) actions.setKhachHang(searchedData.customer);

      detail.setRows(
        documentSelectionRows({
          currentRows: detail.rows,
          process: searchedData.xprocess,
          selectedRows: searchedData.selectedRows
        })
      );
    },
    [actions, detail.rows, detail.setRows]
  );

  const watchCols = detail.rows
    .map((r: any) => `${r.ma_vt_data?.uuid || r.ma_vt}-${r.dvt_data?.uuid || r.dvt}-${r.ma_kho_data?.uuid || r.ma_kho}`)
    .join(',');
  const currentNgayCt = initialData?.ngay_ct || initialFormValues.ngay_ct;

  useEffect(() => {
    let isMounted = true;
    const fetchStock = async () => {
      const chiTiet = detail.rows || [];
      if (!chiTiet.length || !entity?.slug || !currentNgayCt) {
        return;
      }

      try {
        const items = chiTiet
          .map((row: any) => ({
            ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
            dvt: row.dvt_data?.uuid || row.dvt || row.ma_vt_data?.dvt_nl || row.ma_vt_data?.dvt || '',
            ma_kho: row.ma_kho_data?.uuid || row.ma_kho || ''
          }))
          .filter((item: any) => item.ma_vt);

        if (items.length === 0) return;

        const response = await fetchSoDuTonKho(entity.slug, String(currentNgayCt), items);

        if (isMounted) {
          let hasChanges = false;
          const newRows = chiTiet.map((row: any) => {
            const vtUuid = row.ma_vt_data?.uuid || row.ma_vt;
            const khoUuid = row.ma_kho_data?.uuid || row.ma_kho;
            const found = response.results.find(r => r.ma_vt === vtUuid && (r.ma_kho === khoUuid || !r.ma_kho));
            if (found && Number(found.so_du) !== row.sl_ton) {
              hasChanges = true;
              return { ...row, sl_ton: Number(found.so_du) };
            }
            return row;
          });
          if (hasChanges) {
            detail.setRows(newRows);
          }
        }
      } catch (error) {
        console.error('Failed to fetch stock for detail tab', error);
      }
    };

    const timeout = setTimeout(() => {
      fetchStock();
    }, 500);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchCols, currentNgayCt, entity?.slug, detail.setRows]);

  return {
    formMode,
    initialData,
    initialValues: initialData || initialFormValues,
    activeTab,
    tabChange: setActiveTab,
    title: getFormTitle(formMode, activeTab),
    actionButtons: getFormActionButtons(formMode, activeTab, { onAdd, onEdit, onDelete, onCopy, onClose }),
    onClose,
    submit,
    schema,
    formState,
    constraints,
    detail,
    account,
    documents: {
      open: documentsOpen,
      show: () => setDocumentsOpen(true),
      close: () => setDocumentsOpen(false),
      selection: documentSelection,
      columns: getDocumentsColumns()
    }
  };
};

export type FormDialogModel = ReturnType<typeof useFormDialog>;
